// Built-in polyphonic synth used by instrument tracks. Each note is an
// oscillator through a per-voice ADSR gain into a shared low-pass filter →
// output. Plays live from MIDI (computer keyboard or Web MIDI). Works in both
// real-time and OfflineAudioContext (oscillators are supported in both).

/** Common interface for a track's sound generator (built-in Synth or GM). */
export interface Instrument {
  output: AudioNode;
  ready?: Promise<void>;        // sample-loaded instruments (GM); absent for the synth
  noteOn(midi: number, vel?: number): void;
  noteOff(midi: number): void;
  scheduleNote(midi: number, vel: number, startTime: number, dur: number): void;
  allNotesOff(): void;
  dispose(): void;
}

export interface SynthParams {
  wave: OscillatorType;
  attack: number; decay: number; sustain: number; release: number; // seconds / 0..1
  cutoff: number; // Hz, low-pass
  level: number;  // 0..1 master per-note level
}

export const SYNTH_PRESETS: Record<string, SynthParams> = {
  lead:  { wave: "sawtooth", attack: 0.005, decay: 0.10, sustain: 0.70, release: 0.20, cutoff: 6000, level: 0.5 },
  pad:   { wave: "sawtooth", attack: 0.40,  decay: 0.30, sustain: 0.80, release: 0.80, cutoff: 3000, level: 0.4 },
  bass:  { wave: "square",   attack: 0.005, decay: 0.12, sustain: 0.50, release: 0.15, cutoff: 1400, level: 0.6 },
  pluck: { wave: "triangle", attack: 0.002, decay: 0.18, sustain: 0.00, release: 0.12, cutoff: 5000, level: 0.6 },
  organ: { wave: "sine",     attack: 0.01,  decay: 0.00, sustain: 1.00, release: 0.10, cutoff: 8000, level: 0.45 },
};
export const SYNTH_PRESET_LABELS: Record<string, string> = {
  lead: "Synth Lead", pad: "Warm Pad", bass: "Bass", pluck: "Pluck", organ: "Organ",
};

const midiToFreq = (m: number) => 440 * Math.pow(2, (m - 69) / 12);

export class Synth implements Instrument {
  output: GainNode;
  preset: string;
  private filter: BiquadFilterNode;
  private p: SynthParams;
  private voices = new Map<number, { osc: OscillatorNode; g: GainNode }>();
  private scheduled = new Set<OscillatorNode>(); // fire-and-forget playback voices

  constructor(private ctx: BaseAudioContext, preset = "lead", params?: Record<string, number | string>) {
    this.preset = SYNTH_PRESETS[preset] ? preset : "lead";
    this.p = { ...SYNTH_PRESETS[this.preset] };
    if (params) this.applyParams(params);
    this.filter = ctx.createBiquadFilter();
    this.filter.type = "lowpass";
    this.filter.frequency.value = this.p.cutoff;
    this.output = ctx.createGain();
    this.output.gain.value = 1;
    this.filter.connect(this.output);
  }

  params(): SynthParams { return { ...this.p }; }

  setPreset(preset: string) {
    if (!SYNTH_PRESETS[preset]) return;
    this.preset = preset;
    this.p = { ...SYNTH_PRESETS[preset] };
    this.filter.frequency.setTargetAtTime(this.p.cutoff, this.ctx.currentTime, 0.01);
  }

  private applyParams(part: Record<string, number | string>) {
    for (const k of Object.keys(part)) if (k in this.p) (this.p as any)[k] = part[k];
  }
  setParams(part: Partial<SynthParams>) {
    this.applyParams(part as any);
    if (part.cutoff != null) this.filter.frequency.setTargetAtTime(this.p.cutoff, this.ctx.currentTime, 0.01);
  }

  noteOn(midi: number, vel = 1) {
    this.noteOff(midi, true); // retrigger cleanly
    const now = this.ctx.currentTime;
    const osc = this.ctx.createOscillator();
    osc.type = this.p.wave;
    osc.frequency.setValueAtTime(midiToFreq(midi), now);
    const g = this.ctx.createGain();
    const peak = Math.max(0.0001, this.p.level * Math.max(0, Math.min(1, vel)));
    const sus = Math.max(0.0001, peak * this.p.sustain);
    g.gain.setValueAtTime(0.0001, now);
    g.gain.linearRampToValueAtTime(peak, now + this.p.attack);
    g.gain.linearRampToValueAtTime(sus, now + this.p.attack + this.p.decay);
    osc.connect(g); g.connect(this.filter);
    osc.start(now);
    this.voices.set(midi, { osc, g });
  }

  noteOff(midi: number, immediate = false) {
    const v = this.voices.get(midi);
    if (!v) return;
    this.voices.delete(midi);
    const now = this.ctx.currentTime;
    const rel = immediate ? 0.005 : this.p.release;
    try {
      v.g.gain.cancelScheduledValues(now);
      v.g.gain.setValueAtTime(Math.max(0.0001, v.g.gain.value), now);
      v.g.gain.linearRampToValueAtTime(0.0001, now + rel);
    } catch { /* ignore */ }
    try { v.osc.stop(now + rel + 0.02); } catch { /* already stopped */ }
    v.osc.onended = () => { try { v.osc.disconnect(); } catch {} try { v.g.disconnect(); } catch {} };
  }

  /** Schedule a note at absolute ctx time `start` for `dur` seconds (playback).
   *  Independent of the live held-voice map; envelope anchored so any note
   *  length reconstructs correctly. */
  scheduleNote(midi: number, vel: number, start: number, dur: number) {
    const osc = this.ctx.createOscillator();
    osc.type = this.p.wave;
    osc.frequency.setValueAtTime(midiToFreq(midi), start);
    const g = this.ctx.createGain();
    const peak = Math.max(0.0001, this.p.level * Math.max(0, Math.min(1, vel)));
    const sus = Math.max(0.0001, peak * this.p.sustain);
    const { attack: atk, decay: dec, release: rel } = this.p;
    const noteEnd = start + Math.max(0.02, dur);
    g.gain.setValueAtTime(0.0001, start);
    if (noteEnd <= start + atk) {
      g.gain.linearRampToValueAtTime(Math.max(0.0001, peak * (noteEnd - start) / Math.max(1e-4, atk)), noteEnd);
    } else {
      g.gain.linearRampToValueAtTime(peak, start + atk);
      if (noteEnd <= start + atk + dec) {
        const frac = (noteEnd - (start + atk)) / Math.max(1e-4, dec);
        g.gain.linearRampToValueAtTime(peak + (sus - peak) * frac, noteEnd);
      } else {
        g.gain.linearRampToValueAtTime(sus, start + atk + dec);
        g.gain.setValueAtTime(sus, noteEnd);
      }
    }
    g.gain.linearRampToValueAtTime(0.0001, noteEnd + rel);
    osc.connect(g); g.connect(this.filter);
    osc.start(start); osc.stop(noteEnd + rel + 0.02);
    this.scheduled.add(osc);
    osc.onended = () => { this.scheduled.delete(osc); try { osc.disconnect(); } catch {} try { g.disconnect(); } catch {} };
  }

  /** Hard-stop every sounding note (live + scheduled) — transport stop / panic. */
  allNotesOff() {
    const now = this.ctx.currentTime;
    for (const [, v] of this.voices) {
      try { v.g.gain.cancelScheduledValues(now); v.g.gain.setValueAtTime(0.0001, now); } catch {}
      try { v.osc.stop(now + 0.02); } catch {}
    }
    this.voices.clear();
    for (const osc of [...this.scheduled]) { try { osc.stop(now + 0.02); } catch {} }
  }

  dispose() {
    this.allNotesOff();
    try { this.filter.disconnect(); } catch {}
    try { this.output.disconnect(); } catch {}
  }
}
